import { Box, Divider, FormControl, MenuItem, Paper, Select, Typography, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import DonutChart from "../../components/chart/DonutChart";
import HorizontalStackedBarChart from "../../components/chart/HorizontalStackedBarChart";
import PortfolioStatsCard from "../../components/stats/PortfolioStatsCard";
import { Area, AreaChart, ResponsiveContainer } from "recharts";


// ── Styling ──────────────────────────────────────────────────────────────────
const cellTextSx = (isDark) => ({
  fontFamily: GRAPH_PALETTE.font.family,
  fontSize: GRAPH_PALETTE.font.sizes.standard,
  fontWeight: GRAPH_PALETTE.font.weights.standard,
  color: isDark ? "#f1f5f9" : "#1e293b",
});

const filterSelectSx = (isDark, accentColor) => ({
  ...cellTextSx(isDark),
  minWidth: 160,
  "& .MuiSelect-select": { py: "4px", px: 0, pb: "6px" },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
  borderBottom: `2px solid ${accentColor}`,
  borderRadius: 0,
  bgcolor: "transparent",
});

const COLORS = ["#22c55e", "#3b82f6", "#f97316", "#a855f7", "#ec4899", "#14b8a6", "#eab308", "#ef4444", "#06b6d4", "#8b5cf6"];

/**
 * EngagementDashboardGlobalView
 * ─────────────────────────────────────────────────────────────
 * A full analytics dashboard for the Engagement module (Supplier/Client directories).
 * Modeled after DashboardGlobalFootprint.js — includes:
 *   • Horizontal bar chart (top 15 submissions by GWP)
 *   • Donut chart (GWP distribution by company)
 *   • Portfolio stats cards (Mean GWP, SD, Mean Cost)
 *   • Cost efficiency bar chart ($/kg CO₂e)
 *
 * @param {Array}  companies    - Array of { name, id, reports: [...] }
 * @param {string} entityLabel  - "Suppliers" or "Clients"
 * @param {string} accentColor  - "#16a34a" (green) for suppliers, "#2563eb" (blue) for clients
 */
const EngagementDashboardGlobalView = ({ companies = [], entityLabel = "Suppliers", accentColor = "#16a34a" }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [filterCompany, setFilterCompany] = useState("All");
  const [filterLevel, setFilterLevel] = useState("All");

  // ── Derived data ───────────────────────────────────────────────────────────
  const companyNames = useMemo(() => {
    return [...new Set(companies.map((c) => c.name))].sort();
  }, [companies]);

  const allReports = useMemo(() => {
    return companies.flatMap((c) =>
      (c.reports || []).map((r) => ({
        ...r,
        company_name: c.name,
        company_id: c.id,
      }))
    );
  }, [companies]);

  const levelOptions = useMemo(() => {
    return [...new Set(allReports.map((r) => r.level).filter(Boolean))].sort();
  }, [allReports]);

  const filteredReports = useMemo(() => {
    return allReports.filter((r) => {
      if (filterCompany !== "All" && r.company_name !== filterCompany) return false;
      if (filterLevel !== "All" && r.level !== filterLevel) return false;
      return true;
    });
  }, [allReports, filterCompany, filterLevel]);

  // ── Bar chart data (top 15 by GWP) ────────────────────────────────────────
  const barChartData = useMemo(() => {
    return [...filteredReports]
      .filter((r) => (Number(r.gwp) || 0) > 0)
      .sort((a, b) => (b.gwp || 0) - (a.gwp || 0))
      .slice(0, 15)
      .map((r, i) => {
        let name = r.name || `Item ${i + 1}`;
        if (name.length > 45) name = name.substring(0, 45) + "...";
        return { label: name, GWP: Number(r.gwp) || 0 };
      });
  }, [filteredReports]);

  // ── Donut: GWP distribution by company ─────────────────────────────────────
  const companyMetrics = useMemo(() => {
    const totals = {};
    const counts = {};
    filteredReports.forEach((r) => {
      const key = r.company_name || "Unknown";
      totals[key] = (totals[key] || 0) + (Number(r.gwp) || 0);
      counts[key] = (counts[key] || 0) + 1;
    });

    const formatMetrics = (dataMap) => {
      const sorted = Object.entries(dataMap).sort((a, b) => b[1] - a[1]);
      if (sorted.length > 7) {
        const top7 = sorted.slice(0, 7);
        const otherSum = sorted.slice(7).reduce((acc, curr) => acc + curr[1], 0);
        if (otherSum > 0) top7.push(["Other", otherSum]);
        return top7;
      }
      return sorted;
    };

    return {
      gwp: formatMetrics(totals),
      counts: formatMetrics(counts)
    };
  }, [filteredReports]);

  const totalGWP = useMemo(() => companyMetrics.gwp.reduce((a, b) => a + b[1], 0), [companyMetrics]);
  const totalCount = useMemo(() => companyMetrics.counts.reduce((a, b) => a + b[1], 0), [companyMetrics]);

  const donutData = useMemo(() => ({
    gwp: companyMetrics.gwp.map(([label, value], i) => ({
      label,
      value,
      color: COLORS[i % COLORS.length],
      percent: totalGWP > 0 ? (value / totalGWP) * 100 : 0,
    })).filter((d) => d.value > 0),
    counts: companyMetrics.counts.map(([label, value], i) => ({
      label,
      value,
      color: COLORS[i % COLORS.length],
      percent: totalCount > 0 ? (value / totalCount) * 100 : 0,
    })).filter((d) => d.value > 0)
  }), [companyMetrics, totalGWP, totalCount]);

  // ── Cost efficiency chart ($/kg CO₂e) ──────────────────────────────────────
  const costRatioData = useMemo(() => {
    return [...filteredReports]
      .filter((r) => (Number(r.gwp) || 0) > 0)
      .map((r, i) => {
        let name = r.name || `Item ${i + 1}`;
        if (name.length > 45) name = name.substring(0, 45) + "...";
        const gwp = Number(r.gwp) || 1;
        const cost = Number(r.cost) || 0;
        return { label: name, "$/kg CO₂e": cost / gwp };
      })
      .sort((a, b) => b["$/kg CO₂e"] - a["$/kg CO₂e"])
      .slice(0, 15);
  }, [filteredReports]);

  const totalCost = useMemo(() => filteredReports.reduce((sum, r) => sum + (Number(r.cost) || 0), 0), [filteredReports]);


  const filteredMean = useMemo(() => {
    if (filteredReports.length === 0) return 0;
    const total = filteredReports.reduce((acc, r) => acc + (Number(r.gwp) || 0), 0);
    return total / filteredReports.length;
  }, [filteredReports]);

  // ── Leaderboard: Top 5 Active Clients ──────────────────────────────────────
  const topActivityLeaders = useMemo(() => {
    const counts = {};
    filteredReports.forEach((r) => {
      const name = r.name || r.sender_name || r.receiver_name || "Unknown Entity";
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [filteredReports]);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, animation: "org-fadeInUp 0.4s ease" }}>

      {/* ═══════════════════════════════════════════════════════════
          HEADER: Title + Summary Stats
      ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ flex: "1 1 100%", display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: tok.text.primary, fontFamily: GRAPH_PALETTE.font.family, mb: 0.5 }}>
            {t("Global View")}
          </Typography>
          <Typography sx={{ ...cellTextSx(isDark), color: isDark ? "#94a3b8" : "#64748b" }}>
            {t("Cross-entity performance across all")} {t(entityLabel).toLowerCase()} · {totalCount} {t("submissions")}
          </Typography>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          LEFT: Bar chart — Emissions Overview
      ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ flex: "1 1 60%", minWidth: 450 }}>
        <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card }}>
          <Box sx={{ mb: 2, pb: 1, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0 }}>
              {t("Emissions Overview")}
            </Typography>

            {/* Filter Dropdowns */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <FormControl variant="outlined" size="small">
                <Select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} sx={filterSelectSx(isDark, accentColor)} displayEmpty>
                  <MenuItem value="All" sx={cellTextSx(isDark)}>{t("All")} {t(entityLabel)}</MenuItem>
                  {companyNames.map((name) => (
                    <MenuItem key={name} value={name} sx={cellTextSx(isDark)}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {levelOptions.length > 0 && (
                <FormControl variant="outlined" size="small">
                  <Select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)} sx={filterSelectSx(isDark, accentColor)} displayEmpty>
                    <MenuItem value="All" sx={cellTextSx(isDark)}>{t("All Levels")}</MenuItem>
                    {levelOptions.map((lvl) => (
                      <MenuItem key={lvl} value={lvl} sx={cellTextSx(isDark)}>{t(lvl)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>

          {barChartData.length > 0 ? (
            <HorizontalStackedBarChart
              data={barChartData}
              dataKeys={["GWP"]}
              getColor={() => accentColor}
              showLegend={false}
              scale="log"
              domain={[0.0001, "auto"]}
              height={Math.max(barChartData.length * 45 + 100, 350)}
              barSize={20}
              xAxisLabel={t("GWP (kg CO₂e) - Log Scale")}
              yAxisLabel={t("Submissions")}
              showMean={true}
              customMeanValue={filteredMean}
              meanLabel={t("Portfolio Mean")}
            />
          ) : (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center", opacity: 0.5 }}>
              <Typography sx={cellTextSx(isDark)}>{t("No GWP data available.")}</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          RIGHT: Donut + Portfolio Stats
      ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ flex: "1 1 30%", minWidth: 350 }}>
        <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card }}>
          <Box sx={{ mb: 3, pb: 1, borderBottom: `1px solid ${tok.border.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0, fontFamily: GRAPH_PALETTE.font.family }}>
              {t("GWP Distribution by")} {t(entityLabel)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <DonutChart
              data={donutData.gwp}
              size={200}
              strokeWidth={22}
              centerContent={(
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ ...cellTextSx(isDark), display: "block", color: isDark ? "#94a3b8" : "#64748b" }}>{t("Total")}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: tok.text.primary, lineHeight: 1.2 }}>
                    {totalGWP.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography sx={{ ...cellTextSx(isDark), color: isDark ? "#94a3b8" : "#64748b" }}>kg CO₂e</Typography>
                </Box>
              )}
            />
          </Box>

          {/* Legend */}
          <Box sx={{ pr: 1 }}>
            {donutData.gwp.map((d, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: d.color, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>
                      {d.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>
                    {d.value.toLocaleString(undefined, { maximumFractionDigits: 0 })} ({Math.round(d.percent)}%)
                  </Typography>
                </Box>
                <Box sx={{ height: 4, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9", overflow: "hidden" }}>
                  <Box sx={{ height: "100%", width: `${d.percent}%`, bgcolor: d.color, borderRadius: 2, transition: "width 0.6s ease" }} />
                </Box>
              </Box>
            ))}
          </Box>
          <Divider sx={{ mt: 3, mb: 2.5, opacity: 0.35 }} />
          <PortfolioStatsCard items={filteredReports} isDark={isDark} tok={tok} />
        </Paper>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          BOTTOM: Cost Efficiency & Submission Volume Distribution
      ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ flex: "1 1 100%", width: "100%", mt: 1, display: "flex", gap: 3, alignItems: "stretch" }}>

        {/* Left: Cost Efficiency Analysis */}
        <Box sx={{ flex: "1 1 60%", minWidth: 450, display: "flex" }}>
          <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, width: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, display: "flex", flexDirection: "column" }}>
            <Box sx={{ mb: 2, pb: 1, borderBottom: `1px solid ${tok.border.divider}` }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0 }}>
                {t("Cost Efficiency Analysis")}
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              {costRatioData.length > 0 ? (
                <HorizontalStackedBarChart
                  data={costRatioData}
                  dataKeys={["$/kg CO₂e"]}
                  getColor={() => "#f97316"}
                  showLegend={false}
                  scale="log"
                  domain={[0.0001, "auto"]}
                  height={Math.max(costRatioData.length * 45 + 100, 350)}
                  barSize={20}
                  xAxisLabel={t("Cost Efficiency ($/kg CO₂e) - Log Scale")}
                  yAxisLabel={t("Submissions")}
                />
              ) : (
                <Box sx={{ py: 6, display: "flex", justifyContent: "center", opacity: 0.5 }}>
                  <Typography sx={cellTextSx(isDark)}>{t("No cost/GWP data available.")}</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Right: Submission Volume Distribution (Donut) */}
        <Box sx={{ flex: "1 1 30%", minWidth: 350, display: "flex" }}>
          <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, width: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, display: "flex", flexDirection: "column" }}>
            <Box sx={{ mb: 3, pb: 1, borderBottom: `1px solid ${tok.border.divider}` }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0 }}>
                {t("Submission Volume Distribution")}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <DonutChart
                data={donutData.counts}
                size={200}
                strokeWidth={22}
                centerContent={(
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ ...cellTextSx(isDark), display: "block", color: isDark ? "#94a3b8" : "#64748b" }}>{t("Submissions")}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: tok.text.primary, lineHeight: 1.2 }}>
                      {totalCount.toLocaleString()}
                    </Typography>
                    <Typography sx={{ ...cellTextSx(isDark), color: isDark ? "#94a3b8" : "#64748b" }}>{t("Total Reports")}</Typography>
                  </Box>
                )}
              />
            </Box>

            <Box sx={{ pr: 1, flexGrow: 1 }}>
              {donutData.counts.map((d, i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: d.color, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>
                        {d.label}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>
                      {Math.round(d.percent)}%
                    </Typography>
                  </Box>
                  <Box sx={{ height: 4, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9", overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${d.percent}%`, bgcolor: d.color, borderRadius: 2, transition: "width 0.6s ease" }} />
                  </Box>
                </Box>
              ))}
            </Box>

            {/* ── Top 5 Activity Leaders ── */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ mb: 2, pb: 1, borderBottom: `1px solid ${tok.border.divider}` }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 500, 
                  color: tok.text.chartTitle, 
                  textAlign: "left", 
                  m: 0, 
                  fontFamily: GRAPH_PALETTE.font.family
                }}>
                  {t("Top 5 Activity Leaders")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {topActivityLeaders.map((client, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>{client.label}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

    </Box>
  );
};

export default EngagementDashboardGlobalView;
